import React, { useState, useEffect, useRef } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'
import { journalCapitalContribution, journalOwnerWithdrawal } from '../lib/journalEngine'

// ─── HELPERS ──────────────────────────────────────────────────────────────────
const fmt = (n) => Math.abs(Math.round(n || 0)).toLocaleString()
const fmtSigned = (n) => {
  const v = Math.round(n || 0)
  return v < 0 ? `(${Math.abs(v).toLocaleString()})` : v.toLocaleString()
}
const months = ['January','February','March','April','May','June','July','August','September','October','November','December']

function SectionRow({ label, value, indent = 0, bold = false, total = false, color }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: `${total ? 10 : 6}px ${total ? 12 : 8}px`,
      paddingLeft: 12 + indent * 16,
      background: total ? 'var(--green-pale)' : 'transparent',
      borderTop: total ? '1px solid var(--border)' : 'none',
      borderBottom: total ? '2px solid var(--green-bright)' : '1px solid rgba(0,0,0,0.04)',
      fontWeight: bold || total ? 700 : 400,
      fontSize: total ? '0.9rem' : '0.82rem',
    }}>
      <span style={{ color: color || (total ? 'var(--green-deep)' : 'var(--text)') }}>{label}</span>
      <span style={{ fontFamily: "'DM Mono',monospace", color: color || (total ? 'var(--green-deep)' : 'var(--text)') }}>
        {value !== undefined && value !== null ? `UGX ${fmtSigned(value)}` : ''}
      </span>
    </div>
  )
}

function StatementCard({ title, subtitle, children, onPrint, onExcel }) {
  return (
    <div className="card" style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div className="card-title">{title}</div>
          {subtitle && <div className="card-subtitle">{subtitle}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onExcel && <button className="btn btn-secondary btn-sm" onClick={onExcel}>📊 Export Excel</button>}
          {onPrint && <button className="btn btn-ghost btn-sm" onClick={onPrint}>🖨️ Print</button>}
        </div>
      </div>
      {children}
    </div>
  )
}

export default function FinancialStatements() {
  const { isAdmin } = useAuth()
  const [tab, setTab] = useState('pl')
  const [selYear, setSelYear] = useState(new Date().getFullYear())
  const [selMonth, setSelMonth] = useState(new Date().getMonth())
  const [dateMode, setDateMode] = useState('month') // 'month' | 'range'
  const [startDateCustom, setStartDateCustom] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' }))
  const [endDateCustom, setEndDateCustom] = useState(ugandaDate())
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const printRef = useRef()

  const startDate = dateMode === 'range' ? startDateCustom : `${selYear}-${String(selMonth + 1).padStart(2,'0')}-01`
  const endDate = dateMode === 'range' ? endDateCustom : (() => {
    const lastDay = new Date(selYear, selMonth + 1, 0)
    const y = lastDay.getFullYear()
    const m = String(lastDay.getMonth() + 1).padStart(2,'0')
    const d = String(lastDay.getDate()).padStart(2,'0')
    return `${y}-${m}-${d}`
  })()

  useEffect(() => { loadData() }, [selYear, selMonth, tab, startDateCustom, endDateCustom, dateMode])

  const loadData = async () => {
    setLoading(true)
    try {
      // ── Pull all journal entries up to end of period ──────────────────────
      const { data: allJournals } = await supabase
        .from('journal_entries')
        .select('*')
        .lte('entry_date', endDate)

      const je = allJournals || []

      // Helper: sum all debits to an account
      const dr = (code) => je.filter(e => e.debit_account === code).reduce((s, e) => s + e.amount, 0)
      // Helper: sum all credits to an account
      const cr = (code) => je.filter(e => e.credit_account === code).reduce((s, e) => s + e.amount, 0)
      // Helper: balance (debit-normal accounts: dr - cr; credit-normal: cr - dr)
      const bal = (code, normalBalance = 'Debit') =>
        normalBalance === 'Debit' ? dr(code) - cr(code) : cr(code) - dr(code)

      // ── Also pull period-only journals for P&L ───────────────────────────
      const { data: periodJournals } = await supabase
        .from('journal_entries')
        .select('*')
        .gte('entry_date', startDate)
        .lte('entry_date', endDate)
      const pje = periodJournals || []
      const pdr = (code) => pje.filter(e => e.debit_account === code).reduce((s, e) => s + e.amount, 0)
      const pcr = (code) => pje.filter(e => e.credit_account === code).reduce((s, e) => s + e.amount, 0)

      // ── PROFIT & LOSS (period) ────────────────────────────────────────────
      // Revenue by account
      const lettuceA    = pcr('4101')
      const lettuceB    = pcr('4102')
      const strawberryA = pcr('4103')
      const strawberryB = pcr('4104')
      const otherInvoice = pcr('4105') + pcr('4900')
      const totalRevenue = lettuceA + lettuceB + strawberryA + strawberryB + otherInvoice

      // Cost of Sales
      const seeds          = pdr('5100')
      const nutrients      = pdr('5200')
      const growMedia      = pdr('5300')
      const packaging      = pdr('5400')
      const directLabour   = pdr('5500')
      const cogs           = pdr('5600')
      const totalCOS = seeds + nutrients + growMedia + packaging + directLabour + cogs
      const grossProfit = totalRevenue - totalCOS
      const grossMargin = totalRevenue > 0 ? (grossProfit / totalRevenue * 100) : 0

      // Operating Expenses
      const salaries          = pdr('6100')
      const electricityGeneral = pdr('6200')
      const fuel              = pdr('6300')
      const repairs           = pdr('6400')
      const rent              = pdr('6500')
      const insurance         = pdr('6600')
      const marketing         = pdr('6700')
      const professional      = pdr('6800')
      const bankCharges       = pdr('6900')
      const miscellaneous     = pdr('9000')
      const totalOpex = salaries + electricityGeneral + fuel + repairs + rent +
        insurance + marketing + professional + bankCharges + miscellaneous

      const ebitda = grossProfit - totalOpex

      // Depreciation (period)
      const monthlyDepGreenhouse = pdr('7100')
      const monthlyDepHydro      = pdr('7200')
      const monthlyDepSolar      = pdr('7300')
      const totalDepreciation = pdr('7000') + monthlyDepGreenhouse + monthlyDepHydro + monthlyDepSolar

      const ebit = ebitda - totalDepreciation

      // Finance costs
      const interestExpense = pdr('8100')
      const netProfit = ebit - interestExpense

      // ── BALANCE SHEET (all-time as of endDate) ────────────────────────────
      // ASSETS — all from journal entries
      const cashBank       = Math.max(0, bal('1101', 'Debit'))
      const receivables    = Math.max(0, bal('1201', 'Debit'))
      const shareholderRec = Math.max(0, bal('1220', 'Debit'))
      const rawMaterials   = Math.max(0, bal('1310', 'Debit'))
      const wipBalance     = Math.max(0, bal('1320', 'Debit'))
      const finishedGoods  = Math.max(0, bal('1330', 'Debit'))
      const prepayments    = Math.max(0, bal('1400', 'Debit'))
      const cwip           = Math.max(0, bal('1610', 'Debit'))
      // Capitalised fixed assets (1601-1604 used when CWIP transferred to active)
      const grossFA  = Math.max(0, bal('1601','Debit')) + Math.max(0, bal('1602','Debit')) +
                       Math.max(0, bal('1603','Debit')) + Math.max(0, bal('1604','Debit'))
      const accumDep = Math.max(0, bal('1650', 'Credit'))
      const netFA    = grossFA - accumDep

      const totalAssets = cashBank + receivables + shareholderRec + rawMaterials + wipBalance +
        finishedGoods + prepayments + cwip + netFA

      // LIABILITIES
      const tradePayables  = Math.max(0, bal('2101', 'Credit'))  // Supplier Payables
      const loanTotal      = Math.max(0, bal('2201', 'Credit'))  // Loans Payable
      const accruedInterest = Math.max(0, bal('2401', 'Credit')) // Accrued Interest
      const accruedSalaries = Math.max(0, bal('2402', 'Credit')) // Accrued Salaries
      const accruedExpenses = accruedInterest + accruedSalaries
      const totalLiabilities = tradePayables + loanTotal + accruedExpenses

      // EQUITY — per spec
      // Share Capital (issued but unpaid): Cr 3100 = 5M
      const shareCapital        = Math.max(0, bal('3100', 'Credit'))
      // Capital Contributions (actual cash in): Cr 3300
      const equityContributions = Math.max(0, bal('3300', 'Credit'))
      // Withdrawals: Dr 3400
      const equityWithdrawals   = Math.max(0, dr('3400'))

      // Retained Earnings = All-time Revenue - All-time COGS - All-time Opex - Depreciation - Interest
      // Read from journal entries (all income accounts credit, all expense accounts debit)
      const allTimeRevenue = cr('4101') + cr('4102') + cr('4103') + cr('4104') + cr('4105') + cr('4900')
      const allTimeCOS     = dr('5100') + dr('5200') + dr('5300') + dr('5400') + dr('5500') + dr('5600')
      const allTimeOpex    = dr('6100') + dr('6200') + dr('6300') + dr('6400') + dr('6500') +
                             dr('6600') + dr('6700') + dr('6800') + dr('6900') + dr('9000')
      const allTimeDep     = dr('7000') + dr('7100') + dr('7200') + dr('7300')
      const allTimeInterest = dr('8100')
      const retainedEarnings = allTimeRevenue - allTimeCOS - allTimeOpex - allTimeDep - allTimeInterest

      // Closing Equity = Share Capital + Contributions - Withdrawals + Retained Earnings
      const totalEquity = shareCapital + equityContributions - equityWithdrawals + retainedEarnings

      // Validation
      const balanceCheck = Math.abs(totalAssets - (totalLiabilities + totalEquity))
      const cashBreakdown = {
        opening: 0,
        totalIn: je.filter(e => e.debit_account === '1101').reduce((s, e) => s + e.amount, 0),
        totalOut: je.filter(e => e.credit_account === '1101').reduce((s, e) => s + e.amount, 0),
      }

      // ── CASH FLOW (period) ────────────────────────────────────────────────
      const cashFromCustomers = pje.filter(e => e.debit_account === '1101' && ['invoice','customer_payment'].includes(e.module)).reduce((s, e) => s + e.amount, 0)
      const cashToSuppliers   = pje.filter(e => e.credit_account === '1101' && ['payables','cash_expense'].includes(e.module)).reduce((s, e) => s + e.amount, 0)
      const netOperating = cashFromCustomers - cashToSuppliers

      const assetPayments = pje.filter(e => e.module === 'fixed_asset' && e.credit_account === '1101').reduce((s, e) => s + e.amount, 0)
      const netInvesting = -assetPayments

      const loanReceipts  = pje.filter(e => e.module === 'loan' && e.debit_account === '1101').reduce((s, e) => s + e.amount, 0)
      const loanRepayments = pje.filter(e => e.module === 'loan_repayment' && e.credit_account === '1101').reduce((s, e) => s + e.amount, 0)
      const netFinancing = loanReceipts - loanRepayments
      const netCashMovement = netOperating + netInvesting + netFinancing

      // ── TRIAL BALANCE — opening / period movement / closing ──────────────
      const { data: coa } = await supabase.from('chart_of_accounts').select('*').order('code')
      const coaMap = {}
      ;(coa || []).forEach(a => { coaMap[a.code] = a })

      // Pull journals BEFORE the period for opening balances
      const { data: openingJournals } = await supabase
        .from('journal_entries').select('debit_account, credit_account, amount')
        .lt('entry_date', startDate)
      const oje = openingJournals || []

      // All codes across all time
      const allCodes = [...new Set([
        ...je.map(e => e.debit_account), ...je.map(e => e.credit_account),
        ...oje.map(e => e.debit_account), ...oje.map(e => e.credit_account),
      ])].sort()

      const tbRows = allCodes.map(code => {
        const n = parseInt(code)
        const inferredCategory = n < 2000 ? 'Asset' : n < 3000 ? 'Liability' : n < 4000 ? 'Equity' : n < 5000 ? 'Income' : 'Expense'
        const inferredNormalBalance = ['Liability','Equity','Income'].includes(inferredCategory) ? 'Credit' : 'Debit'
        const account = coaMap[code] || { code, name: code, category: inferredCategory, normal_balance: inferredNormalBalance }
        // Opening (before period)
        const openDr = oje.filter(e => e.debit_account === code).reduce((s, e) => s + e.amount, 0)
        const openCr = oje.filter(e => e.credit_account === code).reduce((s, e) => s + e.amount, 0)
        const openingBalance = account.normal_balance === 'Debit' ? openDr - openCr : openCr - openDr
        // Period movement
        const moveDr = pje.filter(e => e.debit_account === code).reduce((s, e) => s + e.amount, 0)
        const moveCr = pje.filter(e => e.credit_account === code).reduce((s, e) => s + e.amount, 0)
        // Closing (all-time up to endDate)
        const totalDr = je.filter(e => e.debit_account === code).reduce((s, e) => s + e.amount, 0)
        const totalCr = je.filter(e => e.credit_account === code).reduce((s, e) => s + e.amount, 0)
        const closingBalance = account.normal_balance === 'Debit' ? totalDr - totalCr : totalCr - totalDr
        return { ...account, openingBalance, moveDr, moveCr, totalDr, totalCr, closingBalance }
      }).filter(r => r.totalDr > 0 || r.totalCr > 0 || r.openingBalance !== 0)

      const tbTotalDr = tbRows.reduce((s, r) => s + r.totalDr, 0)
      const tbTotalCr = tbRows.reduce((s, r) => s + r.totalCr, 0)
      const tbPeriodDr = tbRows.reduce((s, r) => s + (r.moveDr || 0), 0)
      const tbPeriodCr = tbRows.reduce((s, r) => s + (r.moveCr || 0), 0)
      const tbOpeningTotal = tbRows.reduce((s, r) => s + (r.openingBalance || 0), 0)
      const tbClosingTotal = tbRows.reduce((s, r) => s + (r.closingBalance || 0), 0)

      setData({
        // P&L
        lettuceA, lettuceB, strawberryA, strawberryB, otherInvoice,
        totalRevenue, seeds, nutrients, growMedia, packaging, directLabour, cogs, totalCOS,
        grossProfit, grossMargin,
        salaries, electricityGeneral, fuel, repairs, rent, insurance,
        marketing, professional, bankCharges, miscellaneous, totalOpex,
        ebitda, monthlyDepGreenhouse, monthlyDepHydro, monthlyDepSolar, totalDepreciation,
        ebit, interestExpense, netProfit,
        // Balance Sheet — Assets
        cashBank, cashBreakdown, receivables, shareholderRec, prepayments,
        rawMaterials, wipBalance, finishedGoods, grossFA, accumDep, netFA, cwip,
        totalAssets,
        // Balance Sheet — Liabilities
        tradePayables, loanTotal, accruedInterest, accruedSalaries, accruedExpenses,
        totalLiabilities,
        // Balance Sheet — Equity
        shareCapital, equityContributions, equityWithdrawals,
        retainedEarnings, totalEquity, equity: totalEquity,
        balanceCheck,
        // Cash Flow
        cashFromCustomers, cashToSuppliers, netOperating,
        assetPayments, netInvesting,
        loanReceipts, loanRepayments: loanRepayments, netFinancing,
        netCashMovement,
        // Trial Balance
        tbRows, tbTotalDr, tbTotalCr, tbPeriodDr, tbPeriodCr, tbOpeningTotal, tbClosingTotal,
      })
    } catch (err) {
      console.error('Financial statements error:', err)
    }
    setLoading(false)
  }


  const handlePrint = () => window.print()

  const handleExcelExport = (statementName) => {
    if (!data) return
    let csv = ''
    const period = `${months[selMonth]} ${selYear}`

    if (statementName === 'pl') {
      csv = `Kobflo Greentek Ltd - Profit & Loss Statement\n${period}\n\n`
      csv += `REVENUE\n`
      csv += `Lettuce Sales Grade A,${data.lettuceA}\n`
      csv += `Lettuce Sales Grade B,${data.lettuceB}\n`
      csv += `Strawberry Sales Grade A,${data.strawberryA}\n`
      csv += `Strawberry Sales Grade B,${data.strawberryB}\n`
      csv += `Other Income,${data.otherInvoice}\n`
      csv += `Total Revenue,${data.totalRevenue}\n\n`
      csv += `COST OF SALES\n`
      csv += `Seeds & Seedlings,${data.seeds}\n`
      csv += `Nutrients & Chemicals,${data.nutrients}\n`
      csv += `Total Cost of Sales,${data.totalCOS}\n\n`
      csv += `GROSS PROFIT,${data.grossProfit}\n`
      csv += `Gross Margin %,${data.grossMargin.toFixed(1)}%\n\n`
      csv += `OPERATING EXPENSES\n`
      csv += `Salaries,${data.salaries}\n`
      csv += `Fuel & Vehicle,${data.fuel}\n`
      csv += `Total Operating Expenses,${data.totalOpex}\n\n`
      csv += `EBITDA,${data.ebitda}\n`
      csv += `Total Depreciation,${data.totalDepreciation}\n`
      csv += `EBIT (Operating Profit),${data.ebit}\n`
      csv += `Interest Expense,${data.interestExpense}\n`
      csv += `NET PROFIT / LOSS,${data.netProfit}\n`
    } else if (statementName === 'bs') {
      csv = `Kobflo Greentek Ltd - Balance Sheet\nAs at ${endDate}\n\n`
      csv += `ASSETS\n`
      csv += `Cash at Bank,${data.cashBank}\n`
      csv += `Accounts Receivable (Customers),${data.receivables}\n`
      csv += `Other Receivables (Unpaid Share Capital),${data.shareholderRec}\n`
      csv += `Raw Materials,${data.rawMaterials}\n`
      csv += `Work in Progress,${data.wipBalance}\n`
      csv += `Finished Goods,${data.finishedGoods}\n`
      csv += `Capital Work in Progress,${data.cwip}\n`
      csv += `Fixed Assets at Cost,${data.grossFA}\n`
      csv += `Less: Accumulated Depreciation,${-data.accumDep}\n`
      csv += `Net Book Value,${data.netFA}\n`
      csv += `TOTAL ASSETS,${data.totalAssets}\n\n`
      csv += `LIABILITIES\n`
      csv += `Supplier Payables,${data.tradePayables}\n`
      csv += `Loans Payable,${data.loanTotal}\n`
      csv += `Accrued Interest,${data.accruedInterest}\n`
      csv += `TOTAL LIABILITIES,${data.totalLiabilities}\n\n`
      csv += `EQUITY\n`
      csv += `Share Capital,${data.shareCapital}\n`
      csv += `Capital Contributions,${data.equityContributions}\n`
      csv += `Owner Withdrawals,${-data.equityWithdrawals}\n`
      csv += `Retained Earnings / (Loss),${data.retainedEarnings}\n`
      csv += `TOTAL EQUITY,${data.totalEquity}\n\n`
      csv += `TOTAL LIABILITIES + EQUITY,${data.totalLiabilities + data.totalEquity}\n`
      csv += `Balance Check (should be 0),${data.totalAssets - (data.totalLiabilities + data.totalEquity)}\n`
    } else {
      csv = `Kobflo Greentek Ltd - Cash Flow Statement\n${period}\n\n`
      csv += `OPERATING ACTIVITIES\n`
      csv += `Cash received from customers,${data.cashFromCustomers}\n`
      csv += `Cash paid to suppliers,${-data.cashToSuppliers}\n`
      csv += `Net Operating Cash Flow,${data.netOperating}\n\n`
      csv += `INVESTING ACTIVITIES\n`
      csv += `Asset purchases,${-data.assetPayments}\n`
      csv += `Net Investing Cash Flow,${data.netInvesting}\n\n`
      csv += `FINANCING ACTIVITIES\n`
      csv += `Loan repayments,${-data.repayments}\n`
      csv += `Net Financing Cash Flow,${data.netFinancing}\n\n`
      csv += `NET CASH MOVEMENT,${data.netCashMovement}\n`
    }

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Kobflo_${statementName.toUpperCase()}_${months[selMonth]}_${selYear}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleTrialBalanceExcel = async () => {
    if (!data) return
    // Use SheetJS for proper Excel formatting
    if (!window.XLSX) {
      await new Promise((resolve, reject) => {
        const s = document.createElement('script')
        s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js'
        s.onload = resolve; s.onerror = reject
        document.head.appendChild(s)
      })
    }
    const XLSX = window.XLSX
    const wb = XLSX.utils.book_new()

    const inferCategory = (code) => {
      const n = parseInt(code)
      if (n >= 1000 && n < 2000) return 'Asset'
      if (n >= 2000 && n < 3000) return 'Liability'
      if (n >= 3000 && n < 4000) return 'Equity'
      if (n >= 4000 && n < 5000) return 'Income'
      if (n >= 5000 && n < 9999) return 'Expense'
      return 'Other'
    }

    const rows = [
      ['Kobflo Greentek Ltd — Trial Balance'],
      [`Period: ${startDate} to ${endDate}`],
      [`Generated: ${endDate}`],
      [],
      ['Code', 'Account Name', 'Category', 'Opening Balance', 'Dr Movement', 'Cr Movement', 'Closing Balance'],
      ...(data.tbRows||[]).map(r => [
        r.code,
        r.name && r.name !== r.code ? r.name : r.code,
        r.category && r.category !== 'Unknown' ? r.category : inferCategory(r.code),
        r.openingBalance || 0,
        r.moveDr || 0,
        r.moveCr || 0,
        r.closingBalance || 0,
      ]),
      [],
      ['', 'PERIOD TOTALS', '', '', data.tbPeriodDr||0, data.tbPeriodCr||0, ''],
      ['', 'ALL-TIME TOTALS (Dr = Cr check)', '', '', data.tbTotalDr||0, data.tbTotalCr||0, ''],
      ['', Math.abs((data.tbTotalDr||0)-(data.tbTotalCr||0)) < 1 ? 'BALANCED ✓' : 'OUT OF BALANCE ✗', '', '', '', '', ''],
    ]

    const ws = XLSX.utils.aoa_to_sheet(rows)
    ws['!cols'] = [{ wch:8 },{ wch:35 },{ wch:12 },{ wch:18 },{ wch:18 },{ wch:18 },{ wch:18 }]
    ws['!merges'] = [{ s:{r:0,c:0}, e:{r:0,c:6} }, { s:{r:1,c:0}, e:{r:1,c:6} }]
    XLSX.utils.book_append_sheet(wb, ws, 'Trial Balance')
    XLSX.writeFile(wb, `Kobflo_TrialBalance_${startDate}_to_${endDate}.xlsx`)
  }

  const period = `${months[selMonth]} ${selYear}`

  return (
    <AppLayout title="Financial Statements">
      <div className="section-header">
        <div className="section-icon">📊</div>
        <div className="section-info">
          <h2>Financial Statements</h2>
          <p>Monthly P&L, Balance Sheet, and Cash Flow — based on your live data.</p>
        </div>
      </div>

      {/* Period selector */}
      <div className="card" style={{ marginBottom: 18, padding: '14px 18px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Mode toggle */}
          <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden', border: '1px solid var(--border)' }}>
            <button onClick={() => setDateMode('month')} style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700, background: dateMode === 'month' ? 'var(--green-deep)' : 'white', color: dateMode === 'month' ? 'white' : 'var(--text)', border: 'none', cursor: 'pointer' }}>By Month</button>
            <button onClick={() => setDateMode('range')} style={{ padding: '6px 14px', fontSize: '0.78rem', fontWeight: 700, background: dateMode === 'range' ? 'var(--green-deep)' : 'white', color: dateMode === 'range' ? 'white' : 'var(--text)', border: 'none', cursor: 'pointer' }}>Date Range</button>
          </div>

          {dateMode === 'month' ? (
            <>
              <select className="form-select" style={{ width: 160 }} value={selMonth} onChange={e => setSelMonth(parseInt(e.target.value))}>
                {months.map((m, i) => <option key={i} value={i}>{m}</option>)}
              </select>
              <input
                className="form-input"
                type="number"
                style={{ width: 90 }}
                value={selYear}
                min={2020}
                max={2099}
                onChange={e => {
                  const y = parseInt(e.target.value)
                  if (y >= 2020 && y <= 2099) setSelYear(y)
                }}
              />
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>FROM</label>
                <input className="form-input" type="date" style={{ width: 150 }} value={startDateCustom} onChange={e => setStartDateCustom(e.target.value)} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>TO</label>
                <input className="form-input" type="date" style={{ width: 150 }} value={endDateCustom} onChange={e => setEndDateCustom(e.target.value)} />
              </div>
            </>
          )}

          <button className="btn btn-secondary btn-sm" onClick={loadData}>🔄 Refresh</button>
          <div style={{ marginLeft: 'auto', fontFamily: "'Cormorant Garamond',serif", fontSize: '1rem', fontWeight: 700, color: 'var(--green-deep)' }}>
            {dateMode === 'range' ? `${startDateCustom} → ${endDateCustom}` : period}
          </div>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'pl' ? 'active' : ''}`} onClick={() => setTab('pl')}>📈 Profit & Loss</button>
        <button className={`tab ${tab === 'bs' ? 'active' : ''}`} onClick={() => setTab('bs')}>⚖️ Balance Sheet</button>
        <button className={`tab ${tab === 'cf' ? 'active' : ''}`} onClick={() => setTab('cf')}>💧 Cash Flow</button>
        <button className={`tab ${tab === 'tb' ? 'active' : ''}`} onClick={() => setTab('tb')}>📋 Trial Balance</button>
        <button className={`tab ${tab === 'equity' ? 'active' : ''}`} onClick={() => setTab('equity')}>🏦 Equity</button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 60 }}><span className="spinner dark" /><div style={{ marginTop: 12, color: 'var(--text-muted)' }}>Loading financial data...</div></div>
      : !data ? null
      : (
        <>
          {/* ─── PROFIT & LOSS ─────────────────────────────────────────── */}
          {tab === 'pl' && (
            <StatementCard
              title="Profit & Loss Statement"
              subtitle={`For the month of ${period} · Kobflo Greentek Ltd`}
              onPrint={handlePrint}
              onExcel={() => handleExcelExport('pl')}>

              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)' }}>REVENUE</div>
              <SectionRow label="Lettuce Sales — Grade A" value={data.lettuceA} indent={1} />
              <SectionRow label="Lettuce Sales — Grade B" value={data.lettuceB} indent={1} />
              <SectionRow label="Strawberry Sales — Grade A" value={data.strawberryA} indent={1} />
              <SectionRow label="Strawberry Sales — Grade B" value={data.strawberryB} indent={1} />
              {data.otherInvoice > 0 && <SectionRow label="Other Income" value={data.otherInvoice} indent={1} />}
              <SectionRow label="Total Revenue" value={data.totalRevenue} total bold />

              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)', marginTop: 4 }}>COST OF SALES</div>
              {data.seeds > 0 && <SectionRow label="Seeds & Seedlings" value={-data.seeds} indent={1} />}
              {data.nutrients > 0 && <SectionRow label="Nutrients & Chemicals" value={-data.nutrients} indent={1} />}
              {data.growMedia > 0 && <SectionRow label="Growing Media" value={-data.growMedia} indent={1} />}
              {data.packaging > 0 && <SectionRow label="Packaging Materials" value={-data.packaging} indent={1} />}
              {data.electricityGrowing > 0 && <SectionRow label="Electricity — Growing" value={-data.electricityGrowing} indent={1} />}
              {data.water > 0 && <SectionRow label="Water Costs" value={-data.water} indent={1} />}
              {data.directLabour > 0 && <SectionRow label="Direct Labour" value={-data.directLabour} indent={1} />}
              <SectionRow label="Total Cost of Sales" value={-data.totalCOS} total bold />

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 12px', background: data.grossProfit >= 0 ? 'var(--green-pale)' : 'var(--red-pale)', borderTop: '2px solid', borderColor: data.grossProfit >= 0 ? 'var(--green-bright)' : 'var(--red)', marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontFamily: "'Cormorant Garamond',serif", fontSize: '1rem' }}>GROSS PROFIT</span>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontWeight: 700, fontFamily: "'DM Mono',monospace", color: data.grossProfit >= 0 ? 'var(--green-mid)' : 'var(--red)' }}>UGX {fmtSigned(data.grossProfit)}</span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 8 }}>({data.grossMargin.toFixed(1)}%)</span>
                </div>
              </div>

              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)', marginTop: 4 }}>OPERATING EXPENSES</div>
              {data.salaries > 0 && <SectionRow label="Management Salaries" value={-data.salaries} indent={1} />}
              {data.electricityGeneral > 0 && <SectionRow label="Electricity — General" value={-data.electricityGeneral} indent={1} />}
              {data.fuel > 0 && <SectionRow label="Fuel & Vehicle" value={-data.fuel} indent={1} />}
              {data.repairs > 0 && <SectionRow label="Equipment Repairs & Maintenance" value={-data.repairs} indent={1} />}
              {data.rent > 0 && <SectionRow label="Rent" value={-data.rent} indent={1} />}
              {data.insurance > 0 && <SectionRow label="Insurance" value={-data.insurance} indent={1} />}
              {data.marketing > 0 && <SectionRow label="Marketing & Delivery" value={-data.marketing} indent={1} />}
              {data.professional > 0 && <SectionRow label="Professional Fees" value={-data.professional} indent={1} />}
              {data.bankCharges > 0 && <SectionRow label="Bank Charges" value={-data.bankCharges} indent={1} />}
              {data.subscriptions > 0 && <SectionRow label="Subscriptions & Software" value={-data.subscriptions} indent={1} />}
              {data.training > 0 && <SectionRow label="Staff Training" value={-data.training} indent={1} />}
              {data.miscellaneous > 0 && <SectionRow label="Miscellaneous" value={-data.miscellaneous} indent={1} />}
              <SectionRow label="Total Operating Expenses" value={-data.totalOpex} total bold />

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 12px', background: 'var(--cream)', borderTop: '1px solid var(--border)', marginTop: 4 }}>
                <span style={{ fontWeight: 700 }}>EBITDA</span>
                <span style={{ fontWeight: 700, fontFamily: "'DM Mono',monospace", color: data.ebitda >= 0 ? 'var(--green-mid)' : 'var(--red)' }}>UGX {fmtSigned(data.ebitda)}</span>
              </div>

              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)', marginTop: 4 }}>DEPRECIATION</div>
              {data.monthlyDepGreenhouse > 0 && <SectionRow label="Depreciation — Greenhouse Structure" value={-data.monthlyDepGreenhouse} indent={1} />}
              {data.monthlyDepHydro > 0 && <SectionRow label="Depreciation — Hydroponic Systems" value={-data.monthlyDepHydro} indent={1} />}
              {data.monthlyDepSolar > 0 && <SectionRow label="Depreciation — Solar & Electrical" value={-data.monthlyDepSolar} indent={1} />}
              <SectionRow label="Total Depreciation" value={-data.totalDepreciation} total bold />

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 12px', background: 'var(--cream)', borderTop: '1px solid var(--border)', marginTop: 4 }}>
                <span style={{ fontWeight: 700 }}>EBIT (Operating Profit)</span>
                <span style={{ fontWeight: 700, fontFamily: "'DM Mono',monospace", color: data.ebit >= 0 ? 'var(--green-mid)' : 'var(--red)' }}>UGX {fmtSigned(data.ebit)}</span>
              </div>

              {data.interestExpense > 0 && (
                <>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)', marginTop: 4 }}>FINANCE COSTS</div>
                  <SectionRow label="Interest Expense" value={-data.interestExpense} indent={1} />
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 12px', background: data.netProfit >= 0 ? 'var(--green-pale)' : 'var(--red-pale)', borderTop: '3px solid', borderColor: data.netProfit >= 0 ? 'var(--green-bright)' : 'var(--red)', marginTop: 8 }}>
                <span style={{ fontWeight: 900, fontFamily: "'Cormorant Garamond',serif", fontSize: '1.1rem', color: 'var(--green-deep)' }}>NET PROFIT / (LOSS)</span>
                <span style={{ fontWeight: 900, fontFamily: "'Cormorant Garamond',serif", fontSize: '1.3rem', color: data.netProfit >= 0 ? 'var(--green-mid)' : 'var(--red)' }}>
                  UGX {fmtSigned(data.netProfit)}
                </span>
              </div>

              {data.totalRevenue === 0 && (
                <div className="alert alert-amber" style={{ marginTop: 12 }}>
                  No revenue recorded for {period} yet. Revenue will appear here once invoices are raised for this month.
                </div>
              )}
            </StatementCard>
          )}

          {/* ─── BALANCE SHEET ──────────────────────────────────────────── */}
          {tab === 'bs' && (
            <StatementCard
              title="Statement of Financial Position"
              subtitle={`As at ${endDate} · Kobflo Greentek Ltd`}
              onPrint={handlePrint}
              onExcel={() => handleExcelExport('bs')}>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                {/* ASSETS */}
                <div>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)' }}>CURRENT ASSETS</div>
                  <SectionRow label="Cash at Bank (Absa)" value={data.cashBank} indent={1} />
                  {data.cashBreakdown && (
                    <div style={{ padding: '4px 12px 8px 24px', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                      Opening balance: UGX {(data.cashBreakdown.opening || 0).toLocaleString()} &nbsp;+&nbsp;
                      Total cash in: UGX {(data.cashBreakdown.totalIn || 0).toLocaleString()} &nbsp;−&nbsp;
                      Total cash out: UGX {(data.cashBreakdown.totalOut || 0).toLocaleString()}
                    </div>
                  )}
                  <SectionRow label="Accounts Receivable (Customers)" value={data.receivables} indent={1} />
                  <SectionRow label="Other Receivables" value={data.shareholderRec} indent={1} />
                  <SectionRow label="Prepayments" value={data.prepayments} indent={1} />
                  <SectionRow label="Total Current Assets" value={data.cashBank + data.receivables + (data.shareholderRec||0) + data.prepayments} total bold />

                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)', marginTop: 8 }}>NON-CURRENT ASSETS</div>
                  <SectionRow label="Capital Work in Progress (CWIP)" value={data.cwip} indent={1} />
                  {data.grossFA > 0 ? (
                    <React.Fragment>
                      <SectionRow label="Fixed Assets at Cost" value={data.grossFA} indent={1} />
                      <SectionRow label="Less: Accumulated Depreciation" value={-data.accumDep} indent={1} color="var(--red)" />
                      <SectionRow label="Net Book Value" value={data.netFA} indent={2} bold />
                    </React.Fragment>
                  ) : null}
                  <SectionRow label="Total Non-Current Assets" value={data.cwip + data.netFA} total bold />

                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 12px', background: 'var(--green-pale)', borderTop: '3px solid var(--green-bright)', marginTop: 8 }}>
                    <span style={{ fontWeight: 900, fontFamily: "'Cormorant Garamond',serif", fontSize: '1rem' }}>TOTAL ASSETS</span>
                    <span style={{ fontWeight: 900, fontFamily: "'Cormorant Garamond',serif", fontSize: '1.1rem', color: 'var(--green-deep)' }}>UGX {fmt(data.totalAssets)}</span>
                  </div>
                </div>

                {/* LIABILITIES & EQUITY */}
                <div>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)' }}>CURRENT LIABILITIES</div>
                  <SectionRow label="Trade Payables (Asset Creditors)" value={data.tradePayables} indent={1} />
                  <SectionRow label="Total Current Liabilities" value={data.tradePayables} total bold />

                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)', marginTop: 8 }}>NON-CURRENT LIABILITIES</div>
                  <SectionRow label="Loans Outstanding" value={data.loanTotal} indent={1} />
                  <SectionRow label="Total Non-Current Liabilities" value={data.loanTotal} total bold />

                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 12px', background: 'var(--red-pale)', borderTop: '2px solid var(--red)', marginTop: 8, marginBottom: 16 }}>
                    <span style={{ fontWeight: 700 }}>TOTAL LIABILITIES</span>
                    <span style={{ fontWeight: 700, fontFamily: "'DM Mono',monospace", color: 'var(--red)' }}>UGX {fmt(data.totalLiabilities)}</span>
                  </div>

                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)' }}>EQUITY</div>
                  <SectionRow label="Share Capital (5M issued, unpaid)" value={data.shareCapital} indent={1} />
                  {data.equityContributions > 0 && <SectionRow label="Capital Contributions" value={data.equityContributions} indent={1} />}
                  {data.equityWithdrawals > 0 && <SectionRow label="Owner Withdrawals" value={-data.equityWithdrawals} indent={1} color="var(--red)" />}
                  <SectionRow label="Retained Earnings / (Accumulated Loss)" value={data.retainedEarnings} indent={1} color={data.retainedEarnings >= 0 ? 'var(--green-mid)' : 'var(--red)'} />
                  <SectionRow label="Total Equity" value={data.totalEquity} total bold color={data.totalEquity >= 0 ? 'var(--green-mid)' : 'var(--red)'} />

                  <div style={{ marginTop: 16, padding: 12, background: 'var(--cream)', borderRadius: 8, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    <strong>Check:</strong> Total Assets ({fmt(data.totalAssets)}) = Total Liabilities ({fmt(data.totalLiabilities)}) + Equity ({fmtSigned(data.equity)})
                    <span style={{ marginLeft: 8, color: Math.abs(data.totalAssets - data.totalLiabilities - data.equity) < 1 ? 'var(--green-mid)' : 'var(--red)', fontWeight: 700 }}>
                      {Math.abs(data.totalAssets - data.totalLiabilities - data.equity) < 1 ? '✅ Balances' : '⚠️ Does not balance'}
                    </span>
                  </div>
                </div>
              </div>
            </StatementCard>
          )}

          {/* ─── CASH FLOW ──────────────────────────────────────────────── */}
          {tab === 'cf' && (
            <StatementCard
              title="Cash Flow Statement"
              subtitle={`For the month of ${period} · Kobflo Greentek Ltd`}
              onPrint={handlePrint}
              onExcel={() => handleExcelExport('cf')}>

              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)' }}>OPERATING ACTIVITIES</div>
              <SectionRow label="Cash received from customers" value={data.cashFromCustomers} indent={1} />
              <SectionRow label="Cash paid to suppliers & expenses" value={-data.cashToSuppliers} indent={1} />
              <SectionRow label="Net Cash from Operating Activities" value={data.netOperating} total bold color={data.netOperating >= 0 ? 'var(--green-mid)' : 'var(--red)'} />

              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)', marginTop: 8 }}>INVESTING ACTIVITIES</div>
              <SectionRow label="Payments for capital assets" value={-data.assetPayments} indent={1} />
              <SectionRow label="Net Cash from Investing Activities" value={data.netInvesting} total bold color={data.netInvesting >= 0 ? 'var(--green-mid)' : 'var(--red)'} />

              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.15em', color: 'var(--text-muted)', padding: '8px 12px', background: 'var(--cream)', marginTop: 8 }}>FINANCING ACTIVITIES</div>
              <SectionRow label="Loan repayments made" value={-data.repayments} indent={1} />
              <SectionRow label="Net Cash from Financing Activities" value={data.netFinancing} total bold color={data.netFinancing >= 0 ? 'var(--green-mid)' : 'var(--red)'} />

              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 12px', background: data.netCashMovement >= 0 ? 'var(--green-pale)' : 'var(--red-pale)', borderTop: '3px solid', borderColor: data.netCashMovement >= 0 ? 'var(--green-bright)' : 'var(--red)', marginTop: 8 }}>
                <span style={{ fontWeight: 900, fontFamily: "'Cormorant Garamond',serif", fontSize: '1rem', color: 'var(--green-deep)' }}>NET CHANGE IN CASH</span>
                <span style={{ fontWeight: 900, fontFamily: "'Cormorant Garamond',serif", fontSize: '1.2rem', color: data.netCashMovement >= 0 ? 'var(--green-mid)' : 'var(--red)' }}>
                  UGX {fmtSigned(data.netCashMovement)}
                </span>
              </div>

              {/* Opening and Closing Cash */}
              <div style={{ marginTop: 2, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--border)', background: 'var(--cream)' }}>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Cash at Beginning of Period</span>
                  <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 700, fontSize: '0.9rem' }}>
                    UGX {fmt(data.cashBank - data.netCashMovement)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '14px 16px', background: 'var(--green-pale)' }}>
                  <span style={{ fontWeight: 900, fontFamily: "'Cormorant Garamond',serif", fontSize: '1rem', color: 'var(--green-deep)' }}>CASH AT END OF PERIOD</span>
                  <span style={{ fontWeight: 900, fontFamily: "'Cormorant Garamond',serif", fontSize: '1.2rem', color: data.cashBank >= 0 ? 'var(--green-deep)' : 'var(--red)' }}>
                    UGX {fmt(data.cashBank)}
                  </span>
                </div>
              </div>

              <div className="alert alert-amber" style={{ marginTop: 14 }}>
                <strong>Note:</strong> The cash flow statement reflects transactions recorded in the Daily Cash Log for {period}. For a complete picture, ensure all cash movements are logged daily.
              </div>
            </StatementCard>
          )}

          {/* ─── TRIAL BALANCE ──────────────────────────────────────── */}
          {tab === 'tb' && (
            <StatementCard
              title="Trial Balance"
              subtitle={`${period} · Kobflo Greentek Ltd`}
              onPrint={handlePrint}
              onExcel={() => handleTrialBalanceExcel()}>
              <div className="alert alert-green" style={{ marginBottom: 14, fontSize: '0.78rem' }}>
                ✅ Generated directly from journal entries. Shows opening balance, period movement, and closing balance per account. Total debits = Total credits.
              </div>

              {/* Category collapse state is managed inline */}
              {(() => {
                const categories = ['Asset', 'Liability', 'Equity', 'Income', 'Expense']
                const catColors = { Asset: 'badge-green', Liability: 'badge-red', Equity: 'badge-amber', Income: 'badge-blue', Expense: 'badge-grey' }
                const rows = data.tbRows || []

                return (
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: 55 }}>Code</th>
                          <th>Account Name</th>
                          <th style={{ textAlign: 'right' }}>Opening Balance</th>
                          <th style={{ textAlign: 'right' }}>Period Debits</th>
                          <th style={{ textAlign: 'right' }}>Period Credits</th>
                          <th style={{ textAlign: 'right' }}>Closing Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.map(cat => {
                          const catRows = rows.filter(r => r.category === cat)
                          if (catRows.length === 0) return null
                          const catOpenTotal = catRows.reduce((s, r) => s + (r.openingBalance || 0), 0)
                          const catDrTotal = catRows.reduce((s, r) => s + (r.moveDr || 0), 0)
                          const catCrTotal = catRows.reduce((s, r) => s + (r.moveCr || 0), 0)
                          const catCloseTotal = catRows.reduce((s, r) => s + (r.closingBalance || 0), 0)
                          return (
                            <React.Fragment key={cat}>
                              {/* Category header row */}
                              <tr style={{ background: 'var(--cream)' }}>
                                <td colSpan={2} style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-muted)', padding: '8px 12px', fontWeight: 700 }}>
                                  <span className={`badge ${catColors[cat] || 'badge-grey'}`} style={{ marginRight: 8 }}>{cat}</span>
                                  {catRows.length} account{catRows.length !== 1 ? 's' : ''}
                                </td>
                                <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '0.75rem', padding: '8px 12px', color: 'var(--text-muted)' }}>
                                  {catOpenTotal !== 0 ? catOpenTotal.toLocaleString() : '—'}
                                </td>
                                <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '0.75rem', padding: '8px 12px', color: 'var(--green-deep)' }}>
                                  {catDrTotal > 0 ? catDrTotal.toLocaleString() : '—'}
                                </td>
                                <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '0.75rem', padding: '8px 12px', color: 'var(--text-muted)' }}>
                                  {catCrTotal > 0 ? catCrTotal.toLocaleString() : '—'}
                                </td>
                                <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '0.75rem', padding: '8px 12px', fontWeight: 700 }}>
                                  {catCloseTotal !== 0 ? catCloseTotal.toLocaleString() : '—'}
                                </td>
                              </tr>
                              {/* Account rows */}
                              {catRows.map((r, i) => (
                                <tr key={r.code} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.012)' }}>
                                  <td style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.72rem', color: 'var(--text-muted)', paddingLeft: 20, whiteSpace: 'nowrap' }}>{r.code}</td>
                                  <td style={{ fontWeight: 500, fontSize: '0.82rem', paddingLeft: 24 }}>{r.name || r.code}</td>
                                  <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '0.78rem', color: r.openingBalance < 0 ? 'var(--red)' : 'var(--text-muted)' }}>
                                    {r.openingBalance !== 0 ? `${r.openingBalance < 0 ? '(' : ''}${Math.abs(r.openingBalance).toLocaleString()}${r.openingBalance < 0 ? ')' : ''}` : '—'}
                                  </td>
                                  <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '0.78rem', color: 'var(--green-deep)' }}>
                                    {r.moveDr > 0 ? r.moveDr.toLocaleString() : '—'}
                                  </td>
                                  <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                    {r.moveCr > 0 ? r.moveCr.toLocaleString() : '—'}
                                  </td>
                                  <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontSize: '0.78rem', fontWeight: 600, color: r.closingBalance < 0 ? 'var(--red)' : 'var(--green-deep)' }}>
                                    {r.closingBalance !== 0 ? `${r.closingBalance < 0 ? '(' : ''}${Math.abs(r.closingBalance).toLocaleString()}${r.closingBalance < 0 ? ')' : ''}` : '—'}
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          )
                        })}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: 'var(--cream)', fontWeight: 600, fontSize: '0.78rem' }}>
                          <td colSpan={2} style={{ padding: '8px 12px', color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                            GRAND TOTAL (all categories)
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '8px 12px', fontSize: '0.78rem' }}>
                            {(data.tbOpeningTotal||0).toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '8px 12px', fontSize: '0.78rem', color: 'var(--green-deep)' }}>
                            {(data.tbPeriodDr||0).toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '8px 12px', fontSize: '0.78rem' }}>
                            {(data.tbPeriodCr||0).toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '8px 12px', fontSize: '0.78rem' }}>
                            {(data.tbClosingTotal||0).toLocaleString()}
                          </td>
                        </tr>
                        <tr style={{ background: Math.abs((data.tbPeriodDr||0) - (data.tbPeriodCr||0)) < 1 ? 'var(--green-pale)' : 'var(--red-pale)', fontWeight: 700 }}>
                          <td colSpan={2} style={{ padding: '10px 12px', color: Math.abs((data.tbPeriodDr||0) - (data.tbPeriodCr||0)) < 1 ? 'var(--green-mid)' : 'var(--red)' }}>
                            {Math.abs((data.tbPeriodDr||0) - (data.tbPeriodCr||0)) < 1
                              ? '✅ BALANCED — Period Debits = Period Credits'
                              : `⚠️ Out of balance by UGX ${Math.abs((data.tbPeriodDr||0) - (data.tbPeriodCr||0)).toLocaleString()}`}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '10px 12px', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                            —
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '10px 12px' }}>
                            UGX {(data.tbPeriodDr||0).toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '10px 12px' }}>
                            UGX {(data.tbPeriodCr||0).toLocaleString()}
                          </td>
                          <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '10px 12px', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                            —
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )
              })()}
            </StatementCard>
          )}

          {/* ─── EQUITY ─────────────────────────────────────────────── */}
          {tab === 'equity' && (
            <StatementCard title="Statement of Changes in Equity" subtitle={`For the period ending ${endDate} · Kobflo Greentek Ltd`} onPrint={handlePrint}>
              <div className="alert alert-green" style={{ marginBottom: 16, fontSize: '0.78rem' }}>
                This statement shows how equity has changed since the company was incorporated. Equity = Share Capital + Capital Contributions + Retained Earnings (Accumulated Profit/Loss).
              </div>

              {/* Equity Structure Table */}
              <div className="table-wrap" style={{ marginBottom: 24 }}>
                <table>
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th style={{ textAlign: 'right' }}>Amount (UGX)</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Share Capital */}
                    <tr style={{ background: 'var(--cream)' }}>
                      <td colSpan={3} style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', padding: '8px 12px' }}>SHARE CAPITAL</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: 24 }}>Authorised & Issued Share Capital</td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>5,000,000</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>5,000 shares @ UGX 1,000 each — issued but not yet paid (URSB registration fee pending)</td>
                    </tr>
                    <tr style={{ background: 'var(--green-pale)', fontWeight: 700 }}>
                      <td>Total Share Capital</td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace" }}>5,000,000</td>
                      <td></td>
                    </tr>

                    {/* Capital Contributions */}
                    <tr style={{ background: 'var(--cream)' }}>
                      <td colSpan={3} style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', padding: '8px 12px' }}>CAPITAL CONTRIBUTIONS</td>
                    </tr>
                    {data.equityContributions > 0 ? (
                      <tr>
                        <td style={{ paddingLeft: 24 }}>Additional Capital Injected</td>
                        <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>UGX {(data.equityContributions || 0).toLocaleString()}</td>
                        <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Recorded in Equity page</td>
                      </tr>
                    ) : (
                      <tr>
                        <td style={{ paddingLeft: 24, color: 'var(--text-muted)', fontStyle: 'italic' }}>No additional capital contributions recorded</td>
                        <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace" }}>—</td>
                        <td></td>
                      </tr>
                    )}
                    {data.equityWithdrawals > 0 && (
                      <tr>
                        <td style={{ paddingLeft: 24, color: 'var(--red)' }}>Owner Withdrawals</td>
                        <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", color: 'var(--red)', fontWeight: 700 }}>(UGX {(data.equityWithdrawals || 0).toLocaleString()})</td>
                        <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Recorded in Equity page</td>
                      </tr>
                    )}
                    <tr style={{ background: 'var(--green-pale)', fontWeight: 700 }}>
                      <td>Net Capital Contributions</td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace" }}>UGX {((data.equityContributions || 0) - (data.equityWithdrawals || 0)).toLocaleString()}</td>
                      <td></td>
                    </tr>

                    {/* Retained Earnings */}
                    <tr style={{ background: 'var(--cream)' }}>
                      <td colSpan={3} style={{ fontFamily: "'DM Mono',monospace", fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', padding: '8px 12px' }}>RETAINED EARNINGS / (ACCUMULATED LOSS)</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: 24 }}>Opening Retained Earnings</td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace" }}>—</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Company incorporated in 2026 — no prior period</td>
                    </tr>
                    <tr>
                      <td style={{ paddingLeft: 24 }}>Net Profit / (Loss) for period</td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", fontWeight: 700, color: (data.netProfit || 0) >= 0 ? 'var(--green-mid)' : 'var(--red)' }}>
                        {(data.netProfit || 0) >= 0 ? '' : '('}UGX {Math.abs(data.netProfit || 0).toLocaleString()}{(data.netProfit || 0) < 0 ? ')' : ''}
                      </td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>From P&L statement</td>
                    </tr>
                    <tr style={{ background: (data.retainedEarnings || 0) >= 0 ? 'var(--green-pale)' : 'var(--red-pale)', fontWeight: 700 }}>
                      <td>Closing Retained Earnings</td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", color: (data.retainedEarnings || 0) >= 0 ? 'var(--green-mid)' : 'var(--red)' }}>
                        {(data.retainedEarnings || 0) >= 0 ? '' : '('}UGX {Math.abs(data.retainedEarnings || 0).toLocaleString()}{(data.retainedEarnings || 0) < 0 ? ')' : ''}
                      </td>
                      <td></td>
                    </tr>

                    {/* Total Equity */}
                    <tr style={{ background: 'var(--green-deep)', color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                      <td style={{ padding: '12px' }}>TOTAL EQUITY</td>
                      <td style={{ textAlign: 'right', fontFamily: "'DM Mono',monospace", padding: '12px', color: 'white' }}>
                        UGX {(data.totalEquity || 0).toLocaleString()}
                      </td>
                      <td style={{ padding: '12px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                        Share Capital + Contributions + Retained Earnings
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Verification check */}
              <div style={{ background: 'var(--cream)', borderRadius: 8, padding: 16, fontSize: '0.82rem' }}>
                <div style={{ fontWeight: 700, marginBottom: 8 }}>📐 Balance Sheet Check</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    ['Total Assets', data.totalAssets || 0],
                    ['Total Liabilities', data.totalLiabilities || 0],
                    ['Total Equity', data.totalEquity || 0],
                    ['Liabilities + Equity', (data.totalLiabilities || 0) + (data.totalEquity || 0)],
                  ].map(([label, val]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                      <span style={{ fontFamily: "'DM Mono',monospace", fontWeight: 700 }}>UGX {val.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
                {Math.abs((data.totalAssets || 0) - ((data.totalLiabilities || 0) + (data.totalEquity || 0))) < 1000 ? (
                  <div style={{ marginTop: 10, color: 'var(--green-mid)', fontWeight: 700 }}>✅ Balance sheet balances</div>
                ) : (
                  <div style={{ marginTop: 10, color: 'var(--red)', fontWeight: 700 }}>
                    ⚠️ Difference of UGX {Math.abs((data.totalAssets || 0) - ((data.totalLiabilities || 0) + (data.totalEquity || 0))).toLocaleString()} — check entries
                  </div>
                )}
              </div>
            </StatementCard>
          )}
        </>
      )}
    </AppLayout>
  )
}

// ─── EQUITY TAB ──────────────────────────────────────────────────────────────
function EquityTab({ period, selYear, selMonth }) {
  const { user, isAdmin } = useAuth()
  const [entries, setEntries] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [showForm, setShowForm] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [msg, setMsg] = React.useState(null)
  const [form, setForm] = React.useState({
    entry_date: ugandaDate(),
    type: 'Capital Contribution',
    contributor: '',
    amount: '',
    payment_method: 'Bank Transfer',
    reference: '',
    notes: '',
  })

  React.useEffect(() => { loadEntries() }, [])

  const loadEntries = async () => {
    setLoading(true)
    const { data } = await supabase.from('equity_entries').select('*, profiles(full_name)').order('entry_date', { ascending: false })
    setEntries(data || [])
    setLoading(false)
  }

  const handleSave = async () => {
    if (!form.contributor || !form.amount) { setMsg({ type: 'error', text: 'Contributor name and amount are required.' }); return }
    setSaving(true); setMsg(null)
    const { error } = await supabase.from('equity_entries').insert({
      user_id: user.id,
      entry_date: form.entry_date,
      type: form.type,
      contributor: form.contributor,
      amount: parseFloat(form.amount),
      payment_method: form.payment_method,
      reference: form.reference,
      notes: form.notes,
    })
    if (error) { setMsg({ type: 'error', text: error.message }); setSaving(false); return }
    const amt = parseFloat(form.amount)
    const isInflow = ['Capital Contribution'].includes(form.type)
    const isOutflow = ['Owner Withdrawal'].includes(form.type)
    // Get the inserted entry ID for journal reference
    const { data: savedEntry } = await supabase.from('equity_entries')
      .select('id').eq('contributor', form.contributor).eq('entry_date', form.entry_date)
      .order('created_at', { ascending: false }).limit(1).single()
    const entryId = savedEntry?.id || Date.now().toString()
    if (isInflow) {
      // Dr 1101 Cash / Cr 3300 Capital Contributions
      await journalCapitalContribution({
        entryId, contributor: form.contributor, amount: amt,
        date: form.entry_date, userId: user.id,
      })
      await supabase.from('cash_logs').insert({
        user_id: user.id, log_date: form.entry_date,
        total_income: amt, total_expenses: 0, net: amt,
        income_rows: [{ type: 'equity', description: `Capital contribution — ${form.contributor}`, total: amt }],
        expense_rows: [],
        notes: `Capital contribution by ${form.contributor}.`,
      })
    } else if (isOutflow) {
      // Dr 3400 Withdrawals / Cr 1101 Cash
      await journalOwnerWithdrawal({
        entryId, contributor: form.contributor, amount: amt,
        date: form.entry_date, userId: user.id,
      })
      await supabase.from('cash_logs').insert({
        user_id: user.id, log_date: form.entry_date,
        total_income: 0, total_expenses: amt, net: -amt,
        income_rows: [],
        expense_rows: [{ type: 'equity', description: `Owner withdrawal — ${form.contributor}`, amount: amt }],
        notes: `Owner withdrawal by ${form.contributor}.`,
      })
    }
    setMsg({ type: 'success', text: `Equity entry recorded.${(isInflow||isOutflow) ? ' Journal entry and cash log updated automatically.' : ''}` })
    setSaving(false); setShowForm(false); loadEntries()
    setTimeout(() => setMsg(null), 3000)
  }

  const totalContributions = entries.filter(e => e.type === 'Capital Contribution').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
  const totalWithdrawals = entries.filter(e => e.type === 'Owner Withdrawal').reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
  const netEquity = totalContributions - totalWithdrawals

  const typeColor = { 'Capital Contribution': 'badge-green', 'Owner Withdrawal': 'badge-red', 'Retained Earnings': 'badge-blue', 'Other': 'badge-grey' }

  const handleExcel = () => {
    const rows = [
      ['Kobflo Greentek Ltd — Equity Statement'],
      ['Account', 'Date', 'Type', 'Contributor', 'Amount (UGX)', 'Method', 'Reference'],
      ...entries.map(e => [e.entry_date, e.entry_date, e.type, e.contributor, e.amount, e.payment_method, e.reference || '']),
      [],
      ['Total Capital Contributions', totalContributions],
      ['Total Withdrawals', totalWithdrawals],
      ['Net Equity', netEquity],
    ]
    const csv = rows.map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Kobflo_Equity_Statement.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      {msg && <div className={`alert ${msg.type === 'success' ? 'alert-green' : 'alert-red'}`}>{msg.text}</div>}

      {/* Summary */}
      <div className="kpi-grid" style={{ marginBottom: 16 }}>
        <div className="kpi"><div className="kpi-label">Capital Contributed</div><div className="kpi-value" style={{ fontSize: '1.1rem', color: 'var(--green-mid)' }}>UGX {totalContributions.toLocaleString()}</div></div>
        <div className="kpi"><div className="kpi-label">Owner Withdrawals</div><div className="kpi-value" style={{ fontSize: '1.1rem', color: 'var(--red)' }}>UGX {totalWithdrawals.toLocaleString()}</div></div>
        <div className="kpi"><div className="kpi-label">Net Equity</div><div className="kpi-value" style={{ fontSize: '1.1rem', color: netEquity >= 0 ? 'var(--green-mid)' : 'var(--red)' }}>UGX {netEquity.toLocaleString()}</div></div>
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div className="card-title">Equity & Capital Contributions</div>
            <div className="card-subtitle">Record money coming in from shareholders or owners, and owner withdrawals.</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={handleExcel}>📊 Export CSV</button>
            {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => setShowForm(s => !s)}>+ Record Entry</button>}
          </div>
        </div>

        {showForm && (
          <div style={{ background: 'var(--cream)', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid var(--amber)' }}>
            <div className="grid-2" style={{ marginBottom: 12 }}>
              <div className="form-group">
                <label className="form-label">Entry Type</label>
                <select className="form-select" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                  <option>Capital Contribution</option>
                  <option>Owner Withdrawal</option>
                  <option>Retained Earnings</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input className="form-input" type="date" value={form.entry_date} onChange={e => setForm(f => ({ ...f, entry_date: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Contributor / Shareholder *</label>
                <input className="form-input" value={form.contributor} onChange={e => setForm(f => ({ ...f, contributor: e.target.value }))} placeholder="e.g. Edward Katongole" />
              </div>
              <div className="form-group">
                <label className="form-label">Amount (UGX) *</label>
                <input className="form-input" type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Payment Method</label>
                <select className="form-select" value={form.payment_method} onChange={e => setForm(f => ({ ...f, payment_method: e.target.value }))}>
                  {['Bank Transfer', 'Cash', 'Mobile Money', 'Cheque'].map(m => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Reference / Transaction #</label>
                <input className="form-input" value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} placeholder="e.g. TXN-12345" />
              </div>
            </div>
            <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" style={{ minHeight: 60 }} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : '💾 Save Entry'}</button>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            </div>
          </div>
        )}

        {loading ? <div style={{ textAlign: 'center', padding: 32 }}><span className="spinner dark" /></div>
        : entries.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">🏦</div><h3>No equity entries yet</h3><p>Record capital contributions from shareholders or owner withdrawals here.</p></div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Date</th><th>Type</th><th>Contributor</th><th>Amount (UGX)</th><th>Method</th><th>Reference</th><th>Recorded By</th></tr></thead>
              <tbody>
                {entries.map(e => (
                  <tr key={e.id}>
                    <td style={{ fontWeight: 600 }}>{new Date(e.entry_date).toLocaleDateString('en-UG')}</td>
                    <td><span className={`badge ${typeColor[e.type] || 'badge-grey'}`}>{e.type}</span></td>
                    <td>{e.contributor}</td>
                    <td style={{ fontWeight: 700, color: e.type === 'Owner Withdrawal' ? 'var(--red)' : 'var(--green-mid)', fontFamily: "'DM Mono',monospace" }}>
                      {e.type === 'Owner Withdrawal' ? '(' : '+'}{parseFloat(e.amount).toLocaleString()}{e.type === 'Owner Withdrawal' ? ')' : ''}
                    </td>
                    <td style={{ fontSize: '0.78rem' }}>{e.payment_method}</td>
                    <td style={{ fontSize: '0.78rem', fontFamily: "'DM Mono',monospace", color: 'var(--text-muted)' }}>{e.reference || '—'}</td>
                    <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{e.profiles?.full_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
